import { useState } from 'react';
import { fetchClientImpersonateUrl } from '@/api/clients';
import { Icon } from './Icon';
import { showToast } from '@/lib/toast';
import { useTooltip } from '@/hooks/useTooltip';

interface ImpersonateButtonProps {
  accountId: number;
  service: 'studio' | 'maintest5i';
  /** Только со вкладки "Пользователи" — конкретный пользователь клиента, не обязательно админ. */
  userId?: number;
  disabled: boolean;
}

/**
 * "Вход в Studio"/"Вход в Maintest 5i" — получить готовый URL по клику (не
 * требует сессии в старом /admin, см. докблок ImpersonateUrlService в
 * Laravel) и открыть в новой вкладке. Общий компонент для списка клиентов
 * (одна кнопка на строку, под админа аккаунта) и вкладки "Пользователи"
 * карточки клиента (по кнопке на каждого пользователя).
 */
export function ImpersonateButton({ accountId, service, userId, disabled }: ImpersonateButtonProps) {
  const [loading, setLoading] = useState(false);

  const open = async () => {
    setLoading(true);
    try {
      const { url } = await fetchClientImpersonateUrl(accountId, service, userId);
      window.open(url, '_blank', 'noopener');
    } catch {
      showToast('Не удалось открыть вход.');
    } finally {
      setLoading(false);
    }
  };

  const label = service === 'studio' ? 'Вход в Studio' : 'Вход в Maintest 5i';
  const tooltip = useTooltip<HTMLButtonElement>(label);

  return (
    <button
      ref={tooltip.ref}
      type="button"
      className="icon-btn"
      onClick={open}
      disabled={disabled || loading}
      aria-label={label}
      {...tooltip.handlers}
    >
      <Icon name={disabled ? 'lock' : 'lockOpen'} size={14} />
      {tooltip.node}
    </button>
  );
}
